<script setup lang="ts">
import type { ArticleDetail } from '../../types'

defineProps<{
  details: ArticleDetail
}>()
</script>

<template>
  <div class="article-details">
    <!-- Article texts / descriptions -->
    <section v-if="details.texts?.length || details.videoUrls?.length" class="article-details__section">
      <h4>Beskrivelse</h4>
      <div v-for="(text, i) in details.texts" :key="'t'+i" class="article-details__text" v-html="text" />

      <!-- Videos -->
      <div v-if="details.videoUrls?.length" class="article-details__videos">
        <iframe
          v-for="(url, i) in details.videoUrls"
          :key="'v'+i"
          :src="url"
          width="420"
          height="315"
          frameborder="0"
          allowfullscreen
        ></iframe>
      </div>
    </section>

    <!-- All criteria -->
    <section v-if="details.criteria?.length" class="article-details__section">
      <h4>Spesifikasjoner</h4>
      <table class="article-details__table">
        <tbody>
          <tr v-for="(c, i) in details.criteria" :key="i" :class="{ 'mandatory': c.isMandatory }">
            <td class="article-details__label">{{ c.description }}</td>
            <td class="article-details__value">
              {{ c.value }}<template v-if="c.unit"> {{ c.unit }}</template>
            </td>
          </tr>
        </tbody>
      </table>
    </section>

    <!-- OE Numbers (clickable) -->
    <section v-if="details.oeNumbers?.length" class="article-details__section">
      <h4>OE-nummer</h4>
      <table class="article-details__table">
        <tbody>
          <tr v-for="(oe, i) in details.oeNumbers" :key="i">
            <td class="article-details__label">{{ oe.brandName }}</td>
            <td class="article-details__value">
              <a
                :href="`/personbil?mode=oem&oe=${encodeURIComponent(oe.oeNumber)}`"
                class="article-details__oe-link"
              >
                {{ oe.oeNumber }}
              </a>
            </td>
          </tr>
        </tbody>
      </table>
    </section>

    <!-- GTINs -->
    <section v-if="details.gtins?.length" class="article-details__section">
      <h4>GTIN / Strekkode</h4>
      <ul class="article-details__gtins">
        <li v-for="(gtin, i) in details.gtins" :key="i">{{ gtin }}</li>
      </ul>
    </section>

    <!-- Package info -->
    <section v-if="details.quantityPerPackage > 1" class="article-details__section">
      <p><strong>Antall per pakke:</strong> {{ details.quantityPerPackage }}</p>
    </section>

    <!-- Documents / PDFs (installation instructions, datasheets, etc.) -->
    <section v-if="details.documents?.length" class="article-details__section">
      <h4>Monteringsanvisninger og dokumenter</h4>
      <ul class="article-details__documents">
        <li v-for="doc in details.documents" :key="doc.url">
          <a :href="doc.url" target="_blank" rel="noopener">
            <span class="article-details__doc-meta">
              <span
                v-if="doc.headerDescription"
                class="article-details__doc-header"
              >{{ doc.headerDescription }}</span>
              <span class="article-details__doc-name">{{ doc.fileName || doc.url }}</span>
            </span>
          </a>
        </li>
      </ul>
    </section>

    <!-- Articles this one replaces (predecessors) -->
    <section v-if="details.replaces?.length" class="article-details__section">
      <h4>Erstatter</h4>
      <ul class="article-details__replacements">
        <li v-for="r in details.replaces" :key="'rep-' + r.articleNumber">
          <span class="article-details__rep-number">{{ r.articleNumber }}</span>
          <span v-if="r.mfrName" class="article-details__rep-mfr">{{ r.mfrName }}</span>
        </li>
      </ul>
    </section>

    <!-- Articles that replace this one (successors) -->
    <section v-if="details.replacedBy?.length" class="article-details__section">
      <h4>Erstattes av</h4>
      <ul class="article-details__replacements">
        <li v-for="r in details.replacedBy" :key="'repby-' + r.articleNumber">
          <span class="article-details__rep-number">{{ r.articleNumber }}</span>
          <span v-if="r.mfrName" class="article-details__rep-mfr">{{ r.mfrName }}</span>
        </li>
      </ul>
    </section>
  </div>
</template>

<style scoped lang="scss">
.article-details {
  &__section {
    margin-bottom: 1.25rem;

    h4 {
      font-size: 0.9rem;
      margin-bottom: 0.5rem;
      color: #333;
    }
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.85rem;

    td {
      padding: 4px 8px;
      border-bottom: 1px solid #f3f4f6;
    }

    tr.mandatory td {
      font-weight: 600;
    }
  }

  &__label {
    color: #666;
    width: 45%;
  }

  &__value {
    font-weight: 500;
  }

  &__oe-link {
    color: #002868;
    text-decoration: underline;
    cursor: pointer;

    &:hover { color: #001d4f; }
  }

  &__text {
    font-size: 0.9rem;
    line-height: 1.5;
    margin-bottom: 0.5rem;
  }

  &__videos {
    margin-top: 1rem;
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;

    iframe {
      max-width: 100%;
      border-radius: 6px;
    }
  }

  &__gtins {
    list-style: none;
    padding: 0;
    font-family: monospace;
    font-size: 0.85rem;

    li { margin-bottom: 0.25rem; }
  }

  &__documents {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    li { margin: 0; }

    a {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.5rem 0.75rem;
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      color: #002868;
      text-decoration: none;
      transition: background 0.15s, border-color 0.15s;

      &:hover {
        background: #f9fafb;
        border-color: #002868;
        text-decoration: underline;
      }
    }
  }

  &__doc-meta {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__doc-header {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    color: #666;
  }

  &__doc-name {
    font-size: 0.9rem;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__replacements {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;

    li {
      display: flex;
      align-items: baseline;
      gap: 0.5rem;
      padding: 4px 8px;
      background: #f9fafb;
      border-left: 3px solid #002868;
      border-radius: 2px;
    }
  }

  &__rep-number {
    font-family: monospace;
    font-weight: 600;
    font-size: 0.9rem;
  }

  &__rep-mfr {
    font-size: 0.75rem;
    color: #666;
  }
}
</style>
